import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { logout } from '../../services/api';

const ProfileContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 40px;
  transition: all 0.3s ease;
  background: rgba(255,255,255,0.1);
  
  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #c4a484;
  background: #c4a484;
`;

const UserName = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  min-width: 260px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f8f9fa;
`;

const DropdownAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #c4a484;
  background: #c4a484;
`;

const DropdownUserInfo = styled.div`
  h4 {
    margin: 0 0 5px;
    color: #1a2a3a;
    font-size: 16px;
    font-weight: 600;
  }
  p {
    margin: 0;
    color: #666;
    font-size: 12px;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 20px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &.logout {
    color: #e74c3c;
    border-top: 1px solid #eee;
  }
`;

const ProfileDropdown = ({ user, onLogout, onOpenSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  // Get avatar URL - supports both stored profile_pic and generated avatar
  const getAvatarUrl = () => {
    if (currentUser?.profile_pic) {
      return currentUser.profile_pic;
    }
    const name = currentUser?.name || 'User';
    const initial = name.charAt(0).toUpperCase();
    return `https://ui-avatars.com/api/?background=c4a484&color=fff&name=${initial}&size=50&font-size=24&bold=true`;
  };

  const avatarUrl = getAvatarUrl();
  const displayName = currentUser?.name?.split(' ')[0] || 'User';

  return (
    <ProfileContainer ref={dropdownRef}>
      <ProfileButton onClick={() => setIsOpen(!isOpen)}>
        <Avatar src={avatarUrl} alt={currentUser?.name} />
        <UserName>{displayName}</UserName>
      </ProfileButton>
      
      <DropdownMenu $isOpen={isOpen}>
        <DropdownHeader>
          <DropdownAvatar src={avatarUrl} alt={currentUser?.name} />
          <DropdownUserInfo>
            <h4>{currentUser?.name || 'User'}</h4>
            <p>{currentUser?.email || ''}</p>
          </DropdownUserInfo>
        </DropdownHeader>
        
        <DropdownItem onClick={onOpenSettings}>
          ⚙️ Account Settings
        </DropdownItem>
        
        <DropdownItem className="logout" onClick={handleLogout}>
          🚪 Logout
        </DropdownItem>
      </DropdownMenu>
    </ProfileContainer>
  );
};

export default ProfileDropdown;